import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileForm from './components/ProfileForm';
import AuthWrapper from './components/auth/AuthWrapper';
import Modal from './components/Modal';
import ShareDropdown from './components/ShareDropdown';
import MessagesDropdown from './components/MessagesDropdown';
import ChatWidget from './components/ChatWidget';
import AccountDropdown from './components/AccountDropdown';
import DeleteAccountModal from './components/DeleteAccountModal';
import ThemeToggle from './components/ThemeToggle';
import AdminPanel from './pages/AdminPanel';
import { ProfileData, ChallengesAndNeeds, Speaker, ParallelEvent, Solution, ImpactProgram, PublicPolicy, BusinessRoundParticipant } from './types/profile.types';
import { saveProfile, saveProfileSection, getProfileByEmail, updateDraftStatus } from './services/api';
import { verifySession, getCurrentUser, logout as authLogout, deleteAccount as authDeleteAccount } from './services/auth';
import { ensureSupabaseUrl } from './services/supabase';
import { generateProfilePDF } from './utils/pdfGenerator';
import { useUploadProgress } from './contexts/UploadProgressContext';
import { handleRateLimitError } from './utils/rateLimitHandler';
import { validateProfile, ValidationError } from './utils/profileValidation';
import { useSaveError } from './contexts/SaveErrorContext';
import { useSuccess } from './contexts/SuccessContext';
import { useServerError } from './contexts/ServerErrorContext';
import { useSessionExpired } from './contexts/SessionExpiredContext';
import { useTimeout } from './contexts/TimeoutContext';
import ValidationErrorsModal from './components/ValidationErrorsModal';
import { calculateProfileProgress } from './utils/profileProgress';
import ProxyImage from './components/ProxyImage';
import MoodLogoWithTooltip from './components/MoodLogoWithTooltip';
import { AnalyticsEvents } from './services/analytics';
import './index.css';
import ConnectionErrorBanner from './components/ConnectionErrorBanner';
import {
  canSeeNetworkingTab,
  isNetworkingEntryPromptDismissed,
  markNetworkingEntryPromptDismissed,
  NETWORKING_MAIN_TAB_ID,
  NETWORKING_MAIN_TAB_LABEL,
} from './components/networking/networkingCatalogReturn';

const App: React.FC = () => {
  const navigate = useNavigate();
  
  // Limpiar parámetros de la URL solo en la ruta principal (no tocar /soporte?asUserId=... ni otras rutas)
  if (typeof window !== 'undefined' && window.location.pathname === '/' && window.location.search) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthParams = urlParams.get('success') === 'google_oauth' || urlParams.get('error');
    
    window.history.replaceState({}, document.title, window.location.pathname);
    
    if (hasOAuthParams) {
      const params: Record<string, string> = {};
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
      sessionStorage.setItem('oauthParams', JSON.stringify(params));
    }
  }
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('user');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [initialProfileData, setInitialProfileData] = useState<ProfileData | null>(null);
  const getFormDataRef = useRef<(() => ProfileData) | null>(null);
  const [isFormDataReady, setIsFormDataReady] = useState<boolean>(false);
  const [showSpeakerValidationModal, setShowSpeakerValidationModal] = useState<boolean>(false);
  const [pendingFormData, setPendingFormData] = useState<ProfileData | null>(null);
  const [showValidationErrorsModal, setShowValidationErrorsModal] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [pendingSaveData, setPendingSaveData] = useState<ProfileData | null>(null);
  const [incompleteTabs, setIncompleteTabs] = useState<string[]>([]);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [showDraftModal, setShowDraftModal] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState<boolean>(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState<boolean>(false);
  const [pdfProgress, setPdfProgress] = useState<number>(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [pendingLogout, setPendingLogout] = useState<boolean>(false);
  /** Texto del modal de cambios sin guardar: cierre de sesión vs ir a Networking */
  const [unsavedModalContext, setUnsavedModalContext] = useState<'logout' | 'networking' | null>(null);
  const [currentWantsToManageProfile, setCurrentWantsToManageProfile] = useState<boolean>(false);
  const [showNetworkingEntryPrompt, setShowNetworkingEntryPrompt] = useState(false);
  const [networkingTabOpenSignal, setNetworkingTabOpenSignal] = useState(0);
  
  // Contexto de progreso de subida
  const { isUploading, uploadProgress, uploadFileName, uploadFileCount, currentFileIndex } = useUploadProgress();
  
  // Contextos de modales
  const { showSaveError } = useSaveError();
  const { showSuccess } = useSuccess();
  const { showServerError } = useServerError();
  const { showSessionExpired } = useSessionExpired();
  const { showTimeout } = useTimeout();

  // Wrapper para establecer getFormData correctamente
  const handleSetGetFormData = useCallback((fn: () => ProfileData) => {
    getFormDataRef.current = fn;
    setIsFormDataReady(true);
  }, []);

  // Función para comparar datos iniciales con datos actuales
  const checkForUnsavedChanges = useCallback(() => {
    if (!isFormDataReady || !getFormDataRef.current || !initialProfileData) {
      return false;
    }

    try {
      const currentData = getFormDataRef.current();
      // Comparar datos serializados (sin funciones ni referencias)
      const normalizeData = (data: any): any => {
        if (data === null || data === undefined) return null;
        if (typeof data === 'function') return null;
        if (Array.isArray(data)) {
          return data.map(normalizeData).filter(item => item !== null);
        }
        if (typeof data === 'object') {
          const normalized: any = {};
          for (const key in data) {
            if (data.hasOwnProperty(key) && typeof data[key] !== 'function') {
              normalized[key] = normalizeData(data[key]);
            }
          }
          return normalized;
        }
        return data;
      };

      const normalizedInitial = normalizeData(initialProfileData);
      const normalizedCurrent = normalizeData(currentData);

      return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent);
    } catch (error) {
      // Error al verificar cambios sin guardar - silenciado para no exponer información al usuario
      return false;
    }
  }, [isFormDataReady, initialProfileData]);

  const requestNavigateToNetworking = useCallback(
    (proceed: () => void) => {
      if (!checkForUnsavedChanges()) {
        proceed();
        return;
      }
      setPendingNavigation(() => proceed);
      setUnsavedModalContext('networking');
      setShowUnsavedChangesModal(true);
    },
    [checkForUnsavedChanges]
  );

  // Callback para recibir cambios en wantsToManageProfile desde ProfileForm
  const handleWantsToManageProfileChange = useCallback((wantsToManage: boolean) => {
    setCurrentWantsToManageProfile(wantsToManage);
  }, []);

  // Verificar cambios sin guardar periódicamente
  useEffect(() => {
    if (!isAuthenticated || !isFormDataReady) {
      setHasUnsavedChanges(false);
      return;
    }

    const interval = setInterval(() => {
      const hasChanges = checkForUnsavedChanges();
      setHasUnsavedChanges(hasChanges);
    }, 1000); // Verificar cada segundo

    return () => clearInterval(interval);
  }, [isAuthenticated, isFormDataReady, checkForUnsavedChanges]);

  // Detectar intentos de salir (beforeunload)
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome requiere returnValue
      return ''; // Algunos navegadores requieren return
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      // Solo verificar si no estamos en proceso de logout
      if (isLoggingOut) {
        setCheckingAuth(false);
        return;
      }
      
      // CRÍTICO: Verificar si acaba de haber un logout (dentro de los últimos 10 segundos)
      // Si es así, NO verificar sesión ni mostrar el modal de sesión expirada
      const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
      const justLoggedOut = logoutTimestamp && (Date.now() - parseInt(logoutTimestamp)) < 10000;
      
      // Si acabamos de hacer logout, simplemente establecer el estado sin verificar
      if (justLoggedOut) {
        setIsAuthenticated(false);
        setUserEmail(null);
        setUserRole('user');
        setUserId(undefined);
        setCheckingAuth(false);
        return;
      }
      
      // IMPORTANTE: Guardar si había usuario ANTES de verificar la sesión
      // Esto permite detectar cuando la sesión expira al cambiar de entorno (producción -> local)
      // porque verifySession() puede limpiar localStorage si hay un 401
      const hadUserBefore = !!localStorage.getItem('user');
      
      // IMPORTANTE: Siempre intentar verificar la sesión, incluso si no hay usuario en localStorage
      // Esto permite limpiar tokens inválidos cuando se cambia de entorno (producción -> local)
      try {
        const result = await verifySession();
        if (result.success && result.user) {
          setIsAuthenticated(true);
          setUserEmail(result.user.email);
          setUserRole(result.user.role || 'user');
          setUserId(result.user.id);
        } else {
          setIsAuthenticated(false);
          setUserEmail(null);
          setUserRole('user');
          setUserId(undefined);
          // Si es un error 401, mostrar modal de sesión expirada SOLO si:
          // 1. NO acabamos de hacer logout
          // 2. Había un usuario antes de verificar (indica que la sesión expiró, no que nunca hubo sesión)
          if ((result as any).status === 401 && !justLoggedOut && hadUserBefore) {
            showSessionExpired();
        }
        }
      } catch (error: any) {
        setIsAuthenticated(false);
        setUserEmail(null);
        setUserRole('user');
        setUserId(undefined);
        // Si es un error 401, mostrar modal de sesión expirada SOLO si:
        // 1. NO acabamos de hacer logout
        // 2. Había un usuario antes de verificar (indica que la sesión expiró, no que nunca hubo sesión)
        if ((error?.status === 401 || error?.statusCode === 401) && !justLoggedOut && hadUserBefore) {
          showSessionExpired();
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [isLoggingOut]);

  // DEBUG: Log estilos del header en mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const logHeaderStyles = () => {
      if (window.innerWidth > 768) return; // Solo en mobile
      
      const headerBar = document.querySelector('.app-header-bar');
      const headerLogo = document.querySelector('.header-logo');
      const moodLogo = document.querySelector('.header-mood-logo');
      const headerActions = document.querySelector('.header-actions');
      
      if (headerBar) {
        const computed = window.getComputedStyle(headerBar);

      }
      
      if (headerLogo) {
        const computed = window.getComputedStyle(headerLogo);

      }
      
      if (moodLogo) {
        const computed = window.getComputedStyle(moodLogo);
        const rect = moodLogo.getBoundingClientRect();

      } else {

        // Buscar todos los elementos 'a' dentro del header
        const allLinks = headerBar?.querySelectorAll('a');

        allLinks?.forEach((link, index) => {

        });
      }
      
      if (headerActions) {
        const computed = window.getComputedStyle(headerActions);
        const rect = headerActions.getBoundingClientRect();

      }
      
    };
    
    // Log después de que el componente se monte
    const timeoutId = setTimeout(logHeaderStyles, 500);
    
    // Log cuando cambie el tamaño de la ventana
    window.addEventListener('resize', logHeaderStyles);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', logHeaderStyles);
    };
  }, [isAuthenticated]);

  // Escuchar eventos de logout desde otras pestañas
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      // Si se detecta un evento de logout en otra pestaña
      if (e.key === 'logoutEvent' && e.newValue) {
        // CRÍTICO: Establecer logoutTimestamp para evitar que se muestre el modal de sesión expirada
        sessionStorage.setItem('logoutTimestamp', Date.now().toString());
        
        // Cerrar sesión en esta pestaña también
        setIsAuthenticated(false);
        setUserEmail(null);
        setUserRole('user');
        setUserId(undefined);
        setInitialProfileData(null);
        
        // Limpiar localStorage
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('oauthParams');
        
        // Remover tema oscuro
        document.documentElement.classList.remove('dark-theme');
        document.documentElement.classList.add('light-theme');
        localStorage.removeItem('theme');
        
        // Limpiar parámetros de la URL
        if (window.location.search) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Función helper para obtener el dialCode de un código de país
  const getDialCode = (codigoPais: string): string => {
    const countries = [
      { code: 'AR', dialCode: '+54' }, { code: 'BR', dialCode: '+55' }, { code: 'CL', dialCode: '+56' },
      { code: 'CO', dialCode: '+57' }, { code: 'MX', dialCode: '+52' }, { code: 'PE', dialCode: '+51' },
      { code: 'UY', dialCode: '+598' }, { code: 'VE', dialCode: '+58' }, { code: 'BO', dialCode: '+591' },
      { code: 'EC', dialCode: '+593' }, { code: 'PY', dialCode: '+595' }, { code: 'CR', dialCode: '+506' },
      { code: 'PA', dialCode: '+507' }, { code: 'GT', dialCode: '+502' }, { code: 'HN', dialCode: '+504' },
      { code: 'SV', dialCode: '+503' }, { code: 'NI', dialCode: '+505' }, { code: 'CU', dialCode: '+53' },
      { code: 'DO', dialCode: '+1' }, { code: 'HT', dialCode: '+509' }, { code: 'JM', dialCode: '+1' },
      { code: 'TT', dialCode: '+1' }, { code: 'US', dialCode: '+1' }, { code: 'CA', dialCode: '+1' },
      { code: 'ES', dialCode: '+34' }, { code: 'IT', dialCode: '+39' }, { code: 'FR', dialCode: '+33' },
      { code: 'DE', dialCode: '+49' }, { code: 'GB', dialCode: '+44' }, { code: 'PT', dialCode: '+351' },
      { code: 'NL', dialCode: '+31' }, { code: 'BE', dialCode: '+32' }, { code: 'CH', dialCode: '+41' },
      { code: 'AT', dialCode: '+43' }, { code: 'SE', dialCode: '+46' }, { code: 'NO', dialCode: '+47' },
      { code: 'DK', dialCode: '+45' }, { code: 'FI', dialCode: '+358' }, { code: 'PL', dialCode: '+48' },
      { code: 'CZ', dialCode: '+420' }, { code: 'HU', dialCode: '+36' }, { code: 'RO', dialCode: '+40' },
      { code: 'BG', dialCode: '+359' }, { code: 'GR', dialCode: '+30' }, { code: 'RU', dialCode: '+7' },
      { code: 'UA', dialCode: '+380' }, { code: 'IE', dialCode: '+353' }, { code: 'IS', dialCode: '+354' },
      { code: 'CN', dialCode: '+86' }, { code: 'JP', dialCode: '+81' }, { code: 'KR', dialCode: '+82' },
      { code: 'IN', dialCode: '+91' }, { code: 'TH', dialCode: '+66' }, { code: 'SG', dialCode: '+65' },
      { code: 'MY', dialCode: '+60' }, { code: 'ID', dialCode: '+62' }, { code: 'PH', dialCode: '+63' },
      { code: 'VN', dialCode: '+84' }, { code: 'TW', dialCode: '+886' }, { code: 'HK', dialCode: '+852' },
      { code: 'BD', dialCode: '+880' }, { code: 'PK', dialCode: '+92' }, { code: 'LK', dialCode: '+94' },
      { code: 'NP', dialCode: '+977' }, { code: 'MM', dialCode: '+95' }, { code: 'KH', dialCode: '+855' },
      { code: 'LA', dialCode: '+856' }, { code: 'MN', dialCode: '+976' }, { code: 'AU', dialCode: '+61' },
      { code: 'NZ', dialCode: '+64' }, { code: 'FJ', dialCode: '+679' }, { code: 'PG', dialCode: '+675' },
      { code: 'NC', dialCode: '+687' }, { code: 'ZA', dialCode: '+27' }, { code: 'EG', dialCode: '+20' },
      { code: 'NG', dialCode: '+234' }, { code: 'KE', dialCode: '+254' }, { code: 'MA', dialCode: '+212' },
      { code: 'TN', dialCode: '+216' }, { code: 'DZ', dialCode: '+213' }, { code: 'GH', dialCode: '+233' },
      { code: 'ET', dialCode: '+251' }, { code: 'UG', dialCode: '+256' }, { code: 'TZ', dialCode: '+255' },
      { code: 'ZW', dialCode: '+263' }, { code: 'BW', dialCode: '+267' }, { code: 'NA', dialCode: '+264' },
      { code: 'ZM', dialCode: '+260' }, { code: 'MW', dialCode: '+265' }, { code: 'MZ', dialCode: '+258' },
      { code: 'MG', dialCode: '+261' }, { code: 'MU', dialCode: '+230' }, { code: 'SC', dialCode: '+248' },
      { code: 'SA', dialCode: '+966' }, { code: 'AE', dialCode: '+971' }, { code: 'QA', dialCode: '+974' },
      { code: 'KW', dialCode: '+965' }, { code: 'BH', dialCode: '+973' }, { code: 'OM', dialCode: '+968' },
      { code: 'JO', dialCode: '+962' }, { code: 'LB', dialCode: '+961' }, { code: 'SY', dialCode: '+963' },
      { code: 'IQ', dialCode: '+964' }, { code: 'IR', dialCode: '+98' }, { code: 'IL', dialCode: '+972' },
      { code: 'TR', dialCode: '+90' }
    ];
    const country = countries.find(c => c.code === codigoPais);
    return country?.dialCode || '+54';
  };

  // Función helper para parsear el teléfono guardado
  const parsePhone = (phone: string): { codigoPais: string; numeroTelefono: string } => {
    if (!phone || !phone.trim()) {
      return { codigoPais: 'AR', numeroTelefono: '' };
    }
    
    // Lista de países con sus códigos de marcado (mismos que PhoneInput)
    const countries = [
      { code: 'AR', dialCode: '+54' }, { code: 'BR', dialCode: '+55' }, { code: 'CL', dialCode: '+56' },
      { code: 'CO', dialCode: '+57' }, { code: 'MX', dialCode: '+52' }, { code: 'PE', dialCode: '+51' },
      { code: 'UY', dialCode: '+598' }, { code: 'VE', dialCode: '+58' }, { code: 'BO', dialCode: '+591' },
      { code: 'EC', dialCode: '+593' }, { code: 'PY', dialCode: '+595' }, { code: 'CR', dialCode: '+506' },
      { code: 'PA', dialCode: '+507' }, { code: 'GT', dialCode: '+502' }, { code: 'HN', dialCode: '+504' },
      { code: 'SV', dialCode: '+503' }, { code: 'NI', dialCode: '+505' }, { code: 'CU', dialCode: '+53' },
      { code: 'DO', dialCode: '+1' }, { code: 'HT', dialCode: '+509' }, { code: 'JM', dialCode: '+1' },
      { code: 'TT', dialCode: '+1' }, { code: 'US', dialCode: '+1' }, { code: 'CA', dialCode: '+1' },
      { code: 'ES', dialCode: '+34' }, { code: 'IT', dialCode: '+39' }, { code: 'FR', dialCode: '+33' },
      { code: 'DE', dialCode: '+49' }, { code: 'GB', dialCode: '+44' }, { code: 'PT', dialCode: '+351' },
      { code: 'NL', dialCode: '+31' }, { code: 'BE', dialCode: '+32' }, { code: 'CH', dialCode: '+41' },
      { code: 'AT', dialCode: '+43' }, { code: 'SE', dialCode: '+46' }, { code: 'NO', dialCode: '+47' },
      { code: 'DK', dialCode: '+45' }, { code: 'FI', dialCode: '+358' }, { code: 'PL', dialCode: '+48' },
      { code: 'CZ', dialCode: '+420' }, { code: 'HU', dialCode: '+36' }, { code: 'RO', dialCode: '+40' },
      { code: 'BG', dialCode: '+359' }, { code: 'GR', dialCode: '+30' }, { code: 'RU', dialCode: '+7' },
      { code: 'UA', dialCode: '+380' }, { code: 'IE', dialCode: '+353' }, { code: 'IS', dialCode: '+354' },
      { code: 'CN', dialCode: '+86' }, { code: 'JP', dialCode: '+81' }, { code: 'KR', dialCode: '+82' },
      { code: 'IN', dialCode: '+91' }, { code: 'TH', dialCode: '+66' }, { code: 'SG', dialCode: '+65' },
      { code: 'MY', dialCode: '+60' }, { code: 'ID', dialCode: '+62' }, { code: 'PH', dialCode: '+63' },
      { code: 'VN', dialCode: '+84' }, { code: 'TW', dialCode: '+886' }, { code: 'HK', dialCode: '+852' },
      { code: 'BD', dialCode: '+880' }, { code: 'PK', dialCode: '+92' }, { code: 'LK', dialCode: '+94' },
      { code: 'NP', dialCode: '+977' }, { code: 'MM', dialCode: '+95' }, { code: 'KH', dialCode: '+855' },
      { code: 'LA', dialCode: '+856' }, { code: 'MN', dialCode: '+976' }, { code: 'AU', dialCode: '+61' },
      { code: 'NZ', dialCode: '+64' }, { code: 'FJ', dialCode: '+679' }, { code: 'PG', dialCode: '+675' },
      { code: 'NC', dialCode: '+687' }, { code: 'ZA', dialCode: '+27' }, { code: 'EG', dialCode: '+20' },
      { code: 'NG', dialCode: '+234' }, { code: 'KE', dialCode: '+254' }, { code: 'MA', dialCode: '+212' },
      { code: 'TN', dialCode: '+216' }, { code: 'DZ', dialCode: '+213' }, { code: 'GH', dialCode: '+233' },
      { code: 'ET', dialCode: '+251' }, { code: 'UG', dialCode: '+256' }, { code: 'TZ', dialCode: '+255' },
      { code: 'ZW', dialCode: '+263' }, { code: 'BW', dialCode: '+267' }, { code: 'NA', dialCode: '+264' },
      { code: 'ZM', dialCode: '+260' }, { code: 'MW', dialCode: '+265' }, { code: 'MZ', dialCode: '+258' },
      { code: 'MG', dialCode: '+261' }, { code: 'MU', dialCode: '+230' }, { code: 'SC', dialCode: '+248' },
      { code: 'SA', dialCode: '+966' }, { code: 'AE', dialCode: '+971' }, { code: 'QA', dialCode: '+974' },
      { code: 'KW', dialCode: '+965' }, { code: 'BH', dialCode: '+973' }, { code: 'OM', dialCode: '+968' },
      { code: 'JO', dialCode: '+962' }, { code: 'LB', dialCode: '+961' }, { code: 'SY', dialCode: '+963' },
      { code: 'IQ', dialCode: '+964' }, { code: 'IR', dialCode: '+98' }, { code: 'IL', dialCode: '+972' },
      { code: 'TR', dialCode: '+90' }
    ];
    
    // Buscar el código de país que coincida al inicio del teléfono
    for (const country of countries) {
      if (phone.trim().startsWith(country.dialCode)) {
        const numeroTelefono = phone.replace(country.dialCode, '').trim();
        return { codigoPais: country.code, numeroTelefono };
      }
    }
    
    // Si no se encuentra, asumir Argentina por defecto
    return { codigoPais: 'AR', numeroTelefono: phone.trim() };
  };

  // Función para transformar datos de BD al formato del formulario
  // Función helper para separar nombre completo en nombre y apellido
  const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName || !fullName.trim()) {
      return { firstName: '', lastName: '' };
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    // El primer elemento es el nombre, el resto es el apellido
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const transformProfileData = useCallback((dbProfile: any): ProfileData => {
    const parsedPhone = parsePhone(dbProfile.phone || '');
    
    // Usar first_name y last_name de la BD
    const firstName = dbProfile.first_name || '';
    const lastName = dbProfile.last_name || '';
    
    return {
      basicInfo: {
        organizationName: dbProfile.organization_name || '',
        organizationType: dbProfile.organization_type || '',
        country: dbProfile.country || '',
        website: dbProfile.website || '',
        industries: dbProfile.industries ? (typeof dbProfile.industries === 'string' ? JSON.parse(dbProfile.industries) : dbProfile.industries) : [],
        eventProgramParticipation: dbProfile.event_program_participation ? (typeof dbProfile.event_program_participation === 'string' ? JSON.parse(dbProfile.event_program_participation) : dbProfile.event_program_participation) : [],
        socialNetworks: dbProfile.social_networks ? (typeof dbProfile.social_networks === 'string' ? JSON.parse(dbProfile.social_networks) : dbProfile.social_networks) : {},
        logo: dbProfile.logo && dbProfile.logo !== null && dbProfile.logo !== '' ? dbProfile.logo : undefined,
        // Parsear si es string JSON, usar directamente si es array
        thematicAxes: (() => {
          const axes = dbProfile.organization_thematic_axes;
          if (!axes) return [];
          let parsedAxes: any[] = [];
          
          if (typeof axes === 'string') {
            try {
              // Intentar parsear como JSON
              const parsed = JSON.parse(axes);
              parsedAxes = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              // Si falla el parseo, puede ser que ya sea un array serializado de otra forma
              parsedAxes = [];
            }
          } else if (Array.isArray(axes)) {
            parsedAxes = axes;
          }
          
          // Limpiar valores inválidos
          return parsedAxes.filter((axis: any) => {
            const axisStr = String(axis);
            return axisStr && 
              axisStr.trim() !== '' && 
              axisStr !== '[' && 
              axisStr !== ']' &&
              !axisStr.startsWith('{') &&
              !axisStr.startsWith('"[');
          });
        })()
      },
      contactInfo: {
        firstName: firstName,
        lastName: lastName,
        position: dbProfile.position || '',
        email: dbProfile.email || userEmail || '',
        phone: dbProfile.phone || '',
        codigoPais: parsedPhone.codigoPais,
        numeroTelefono: parsedPhone.numeroTelefono,
        area: dbProfile.area || '',
        subarea: dbProfile.subarea || '',
        wantsToManageProfile: dbProfile.wants_to_manage_profile || false,
        wantsToParticipateBusinessRounds: dbProfile.wants_to_participate_business_rounds || false,
        // Parsear si es string JSON, usar directamente si es array
        thematicAxes: (() => {
          const axes = dbProfile.personal_thematic_axes;
          if (!axes) return [];
          let parsedAxes: any[] = [];
          
          if (typeof axes === 'string') {
            try {
              // Intentar parsear como JSON
              const parsed = JSON.parse(axes);
              parsedAxes = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              // Si falla el parseo, puede ser que ya sea un array serializado de otra forma
              parsedAxes = [];
            }
          } else if (Array.isArray(axes)) {
            parsedAxes = axes;
          }
          
          // Limpiar valores inválidos
          return parsedAxes.filter((axis: any) => {
            const axisStr = String(axis);
            return axisStr && 
              axisStr.trim() !== '' && 
              axisStr !== '[' && 
              axisStr !== ']' &&
              !axisStr.startsWith('{') &&
              !axisStr.startsWith('"[');
          });
        })()
      },
      catalogSections: (() => {
        try {
          if (!dbProfile.catalog_sections) return [];
          // Si es un string JSON, parsearlo; si ya es un array, usarlo directamente
          let catalogSections = [];
          if (typeof dbProfile.catalog_sections === 'string') {
            catalogSections = JSON.parse(dbProfile.catalog_sections);
          } else {
            catalogSections = dbProfile.catalog_sections || [];
          }
          
          if (!Array.isArray(catalogSections)) return [];
          
          return catalogSections;
        } catch (error) {
          return [];
        }
      })(),
      institutionalDescription: {
        description: dbProfile.description || ''
      },
      // Campos antiguos (deprecados, mantener vacíos para compatibilidad)
      solutions: [],
      impactPrograms: [],
      publicPolicies: [],
      challengesAndNeeds: [],
      initiatives: (() => {
        try {
          // Leer desde el campo consolidado "iniciativas"
          let allInitiatives = [];
          if (dbProfile.iniciativas) {
            if (typeof dbProfile.iniciativas === 'string') {
              allInitiatives = JSON.parse(dbProfile.iniciativas);
            } else {
              allInitiatives = dbProfile.iniciativas;
            }
          }
          
          if (!Array.isArray(allInitiatives)) {
            return [];
          }
          
          return allInitiatives;
        } catch (error) {
          return [];
        }
      })(),
      participationModalities: (() => {
        try {
          if (!dbProfile.participation_modalities) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.participation_modalities === 'string') {
            const parsed = JSON.parse(dbProfile.participation_modalities);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.participation_modalities) ? dbProfile.participation_modalities : [];
        } catch (error) {
          return [];
        }
      })(),
      agreedModality: dbProfile.agreed_modality || undefined,
      modalityObservations: dbProfile.modality_observations || undefined,
      requestInformation: dbProfile.request_information === true || dbProfile.request_information === 1 ? true : (dbProfile.request_information === false || dbProfile.request_information === 0 ? false : undefined),
      requestExpoInformation: dbProfile.request_expo_information === true || dbProfile.request_expo_information === 1 ? true : (dbProfile.request_expo_information === false || dbProfile.request_expo_information === 0 ? false : undefined),
      selectedStands: (() => {
        try {
          if (!dbProfile.selected_stands_updated) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.selected_stands_updated === 'string') {
            const parsed = JSON.parse(dbProfile.selected_stands_updated);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.selected_stands_updated) ? dbProfile.selected_stands_updated : [];
        } catch (error) {
          return [];
        }
      })(),
      selectedStandsUpdated: (() => {
        try {
          if (!dbProfile.selected_stands_updated) return undefined;
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.selected_stands_updated === 'string') {
            const parsed = JSON.parse(dbProfile.selected_stands_updated);
            return Array.isArray(parsed) ? parsed : undefined;
          }
          return Array.isArray(dbProfile.selected_stands_updated) ? dbProfile.selected_stands_updated : undefined;
        } catch (error) {
          return undefined;
        }
      })(),
      confirmedStands: (() => {
        try {
          if (!dbProfile.confirmed_stands) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.confirmed_stands === 'string') {
            const parsed = JSON.parse(dbProfile.confirmed_stands);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.confirmed_stands) ? dbProfile.confirmed_stands : [];
        } catch (error) {
          return [];
        }
      })(),
      speakers: (() => {
        try {
          if (!dbProfile.speakers) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.speakers === 'string') {
            const parsed = JSON.parse(dbProfile.speakers);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.speakers) ? dbProfile.speakers : [];
        } catch (error) {
          return [];
        }
      })(),
      parallelEvents: (() => {
        try {
          if (!dbProfile.parallel_events) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.parallel_events === 'string') {
            const parsed = JSON.parse(dbProfile.parallel_events);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.parallel_events) ? dbProfile.parallel_events : [];
        } catch (error) {
          return [];
        }
      })(),
      businessRoundsParticipants: (() => {
        try {
          if (!dbProfile.business_rounds_participants) return [];
          // Si es un string, parsearlo; si ya es un array, usarlo directamente
          if (typeof dbProfile.business_rounds_participants === 'string') {
            const parsed = JSON.parse(dbProfile.business_rounds_participants);
            return Array.isArray(parsed) ? parsed : [];
          }
          return Array.isArray(dbProfile.business_rounds_participants) ? dbProfile.business_rounds_participants : [];
        } catch (error) {
          return [];
        }
      })(),
      isDraft: dbProfile.is_draft !== undefined ? dbProfile.is_draft : null
    };
  }, [userEmail]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProfileByEmail();
      if (response.success && response.profile) {
        const transformedData = transformProfileData(response.profile);
        setInitialProfileData(transformedData);
      } else if (response.success && !response.profile) {
        setInitialProfileData(null);
      } else {
        setInitialProfileData(null);
      }
    } catch (error) {
      setInitialProfileData(null);
    } finally {
      setLoading(false);
    }
  }, [transformProfileData]);

  // Cargar perfil existente cuando el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated && userEmail) {
      loadProfile();
      // Track inicio de sesión
      AnalyticsEvents.sessionStart();
    }
  }, [isAuthenticated, userEmail, loadProfile]);

  const dismissNetworkingEntryPrompt = useCallback(() => {
    markNetworkingEntryPromptDismissed(userEmail || undefined);
    setShowNetworkingEntryPrompt(false);
  }, [userEmail]);

  const goToNetworkingFromEntryPrompt = useCallback(() => {
    markNetworkingEntryPromptDismissed(userEmail || undefined);
    setShowNetworkingEntryPrompt(false);
    setNetworkingTabOpenSignal((n) => n + 1);
  }, [userEmail]);

  // Aviso al ingresar al perfil (usuarios con pestaña de networking; una vez hasta cerrar con Entendido).
  useEffect(() => {
    if (checkingAuth || !isAuthenticated || !userEmail) {
      setShowNetworkingEntryPrompt(false);
      return;
    }
    if (userRole === 'admin' || userRole === 'editor') {
      setShowNetworkingEntryPrompt(false);
      return;
    }
    if (!canSeeNetworkingTab(userEmail)) {
      setShowNetworkingEntryPrompt(false);
      return;
    }
    if (isNetworkingEntryPromptDismissed(userEmail)) {
      setShowNetworkingEntryPrompt(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('returnBooking') === '1' || params.get('tab') === NETWORKING_MAIN_TAB_ID) {
      return;
    }
    setShowNetworkingEntryPrompt(true);
  }, [checkingAuth, isAuthenticated, userEmail, userRole]);

  // Monitorear cambios en isFormDataReady
  useEffect(() => {
  }, [isFormDataReady]);

  // Detectar cambios en el tema
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    };

    // Verificar tema inicial
    checkTheme();

    // Observar cambios en el tema
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const handleAuthSuccess = async () => {
    // Verificar la sesión para obtener toda la información actualizada del usuario, incluyendo el rol
    try {
      const result = await verifySession();
      if (result.success && result.user) {
        setIsAuthenticated(true);
        setUserEmail(result.user.email);
        setUserRole(result.user.role || 'user');
        setUserId(result.user.id);
      }
    } catch (error) {

      // Si falla, intentar con getCurrentUser como fallback
      const user = getCurrentUser();
      if (user) {
        setIsAuthenticated(true);
        setUserEmail(user.email);
        // Verificar la sesión para obtener el rol completo
        try {
          const result = await verifySession();
          if (result.success && result.user) {
            setUserRole(result.user.role || 'user');
            setUserId(result.user.id);
          }
        } catch (sessionError) {

        }
      }
    }
  };
  
  const handleLogout = async () => {
    // Prevenir múltiples clics
    if (isLoggingOut) {
      return;
    }
    
    // Si hay cambios sin guardar, mostrar modal de confirmación
    if (hasUnsavedChanges) {
      setPendingLogout(true);
      setUnsavedModalContext('logout');
      setShowUnsavedChangesModal(true);
      return;
    }

    await performLogout();
  };

  const performLogout = async () => {
    try {
      // CRÍTICO: Establecer logoutTimestamp ANTES de setIsLoggingOut para evitar race conditions
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());
      setIsLoggingOut(true);
      
      sessionStorage.removeItem('oauthParams');
      
      // Limpiar parámetros de la URL para evitar que AuthWrapper los procese
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      const result = await authLogout();
      
      // Track logout
      AnalyticsEvents.userLogout();
      
      // CRÍTICO: Actualizar estado DESPUÉS de que el servidor confirme el logout
      // Usar un pequeño delay para asegurar que React procese el cambio de estado
      
      // CRÍTICO: Remover tema oscuro al cerrar sesión para que los modales de autenticación no hereden estilos oscuros
      document.documentElement.classList.remove('dark-theme');
      document.documentElement.classList.add('light-theme');
      // Limpiar también el localStorage del tema para que no se restaure automáticamente
      localStorage.removeItem('theme');
      
      // Actualizar estado de forma síncrona
      setIsAuthenticated(false);
      setUserEmail(null);
      setInitialProfileData(null);
      
      // Forzar un re-renderizado adicional usando requestAnimationFrame
      requestAnimationFrame(() => {
        // Asegurar que el estado se actualice
        setIsAuthenticated((prev) => {
          if (prev) {
            return false;
          }
          return prev;
        });
      });
      
      if (!result.success) {
      }
      
    } catch (error) {
      // Aún así, limpiar el estado para mostrar el modal de login
      setIsAuthenticated(false);
      setUserEmail(null);
      setInitialProfileData(null);
      // Asegurar que logoutTimestamp esté establecido incluso si hay error
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());
    } finally {
      // Mantener logoutTimestamp por un tiempo adicional antes de resetear isLoggingOut
      setTimeout(() => {
      setIsLoggingOut(false);
      }, 100);
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) {
      return;
    }

    try {
      setIsDeletingAccount(true);
      
      const result = await authDeleteAccount();
      
      if (result.success) {
        // Limpiar localStorage y sessionStorage primero
        localStorage.clear();
        sessionStorage.clear();
        
        // Reemplazar la entrada del historial para que no se pueda volver atrás
        // Esto elimina la entrada del historial del perfil eliminado
        window.history.replaceState(null, '', window.location.pathname);
        
        // Limpiar todo el estado
        setIsAuthenticated(false);
        setUserEmail(null);
        setInitialProfileData(null);
        setShowDeleteAccountModal(false);
        
        // Limpiar mensajes de error
        setErrorMessage('');
        
      } else {
        setErrorMessage(result.error || 'Error al eliminar la cuenta');
      }
      
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error al eliminar la cuenta');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Función para guardar solo el logo automáticamente
  const handleLogoUpload = useCallback(async (logoUrl: string) => {
    try {
      
      // Obtener los datos ACTUALES del formulario (no initialProfileData que puede estar desactualizado)
      let currentData: ProfileData;
      if (getFormDataRef.current && typeof getFormDataRef.current === 'function') {
        try {
          currentData = getFormDataRef.current();
        } catch (error) {
          currentData = initialProfileData || {
            basicInfo: {
              organizationName: '',
              organizationType: '',
              country: '',
              website: '',
              industries: [],
              eventProgramParticipation: [],
              socialNetworks: {},
              logo: logoUrl
            },
            contactInfo: {
              firstName: '',
              lastName: '',
              position: '',
              email: userEmail || '',
              phone: '',
              codigoPais: 'AR',
              numeroTelefono: '',
              area: '',
              subarea: '',
              thematicAxes: []
            },
            catalogSections: [],
            institutionalDescription: {
              description: ''
            },
            initiatives: [],
            participationModalities: [],
            selectedStands: [],
            speakers: [],
            parallelEvents: []
          };
        }
      } else {
        // Si no hay función disponible, usar initialProfileData o valores por defecto
        currentData = initialProfileData || {
          basicInfo: {
            organizationName: '',
            organizationType: '',
            country: '',
            website: '',
            industries: [],
            eventProgramParticipation: [],
            socialNetworks: {},
            logo: logoUrl,
            thematicAxes: []
          },
          contactInfo: {
            firstName: '',
            lastName: '',
            position: '',
            email: userEmail || '',
            phone: '',
            codigoPais: 'AR',
            numeroTelefono: '',
            area: '',
            subarea: '',
            thematicAxes: []
          },
          catalogSections: [],
          institutionalDescription: {
            description: ''
          },
          initiatives: [],
          participationModalities: [],
          selectedStands: [],
          speakers: [],
          parallelEvents: []
        };
      }

      // Actualizar solo el logo en los datos
      const dataWithLogo: ProfileData = {
        ...currentData,
        basicInfo: {
          ...currentData.basicInfo,
          logo: logoUrl
        },
        contactInfo: {
          ...currentData.contactInfo,
          codigoPais: currentData.contactInfo.codigoPais || 'AR',
          numeroTelefono: currentData.contactInfo.numeroTelefono || ''
        }
      };

      const response = await saveProfile(dataWithLogo);
      
      if (response.success) {
        // ACTUALIZAR initialProfileData con el logo para mantener sincronización
        // Esto evita que cuando se recargue el perfil, se pierda el logo
        setInitialProfileData(prev => {
          if (prev) {
            return {
              ...prev,
              basicInfo: {
                ...prev.basicInfo,
                logo: logoUrl
              }
            };
          }
          return dataWithLogo;
        });
      } else {
        throw new Error(response.error || 'Error al guardar el logo');
      }
    } catch (error) {
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // Función para validar si hay oradores con campos obligatorios incompletos
  const validateSpeakers = (speakers: Speaker[] | undefined): boolean => {
    if (!speakers || speakers.length === 0) {
      return true; // No hay oradores, no hay problema
    }

    // Verificar cada orador
    for (const speaker of speakers) {
      // Campos obligatorios según el formulario:
      // - photo (Foto)
      // - firstName (Nombre)
      // - lastName (Apellido)
      // - nationality (Nacionalidad)
      // - organization (Organización)
      // - position (Cargo o especialidad)
      // - description (Minibiografía)
      // - thematicAxes (Temática de interés para su exposición)
      // - contentDescription (Breve descripción del contenido que desea abordar)

      const hasPhoto = speaker.photo && (
        typeof speaker.photo === 'string' ? speaker.photo.trim() !== '' : true
      );
      const hasFirstName = speaker.firstName && speaker.firstName.toString().trim() !== '';
      const hasLastName = speaker.lastName && speaker.lastName.toString().trim() !== '';
      const hasNationality = speaker.nationality && speaker.nationality.toString().trim() !== '';
      const hasOrganization = speaker.organization && speaker.organization.toString().trim() !== '';
      const hasPosition = speaker.position && speaker.position.toString().trim() !== '';
      const hasDescription = speaker.description && speaker.description.toString().trim() !== '';
      const hasThematicAxes = speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0;
      const hasContentDescription = speaker.contentDescription && speaker.contentDescription.toString().trim() !== '';
      
      // Validar Biografía: obligatorio PERO puede estar vacío si LinkedIn está lleno
      const hasLinkedin = speaker.linkedin && speaker.linkedin.toString().trim() !== '';
      const hasBiography = speaker.biography && speaker.biography.toString().trim() !== '';
      const hasValidBiography = hasLinkedin || hasBiography; // Biografía es válida si tiene LinkedIn O tiene Biografía

      // Si falta algún campo obligatorio, retornar false
      if (!hasPhoto || !hasFirstName || !hasLastName || !hasNationality || 
          !hasOrganization || !hasPosition || !hasDescription || 
          !hasThematicAxes || !hasContentDescription || !hasValidBiography) {
        return false;
      }
    }

    return true; // Todos los oradores tienen campos completos
  };

  // Función para validar si hay eventos paralelos con campos obligatorios incompletos
  const validateParallelEvents = (events: ParallelEvent[] | undefined): boolean => {
    if (!events || events.length === 0) {
      return true; // No hay eventos, no hay problema
    }

    // Verificar cada evento
    for (const event of events) {
      // Campos obligatorios del evento:
      // - name (Título de la actividad)
      // - description (Descripción)
      // - speakerIds/localSpeakers (Oradores - debe tener al menos uno)
      // - duration (Duración)
      // - thematicAxes (Ejes de la actividad - debe tener al menos uno)
      // - attendeeProfiles (Perfil de asistentes - debe tener al menos uno)

      const hasName = event.name && event.name.toString().trim() !== '';
      const hasDescription = event.description && event.description.toString().trim() !== '';
      const hasSpeakers = (event.speakerIds && event.speakerIds.length > 0) || 
                          (event.localSpeakers && event.localSpeakers.length > 0);
      const hasDuration = event.duration && event.duration.toString().trim() !== '';
      const hasThematicAxes = event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0;
      const hasAttendeeProfiles = event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0;

      // Si falta algún campo obligatorio del evento, retornar false
      if (!hasName || !hasDescription || !hasSpeakers || !hasDuration || !hasThematicAxes || !hasAttendeeProfiles) {
        return false;
      }

      // Validar oradores locales si existen
      // NOTA: Los oradores locales NO tienen "Temática de interés" ni "Breve descripción del contenido"
      // Solo se validan estos campos para los oradores de la pestaña "Oradores en congreso"
      if (event.localSpeakers && event.localSpeakers.length > 0) {
        for (const localSpeaker of event.localSpeakers) {
          const hasPhoto = localSpeaker.photo && (
            typeof localSpeaker.photo === 'string' ? localSpeaker.photo.trim() !== '' : true
          );
          const hasFirstName = localSpeaker.firstName && localSpeaker.firstName.toString().trim() !== '';
          const hasLastName = localSpeaker.lastName && localSpeaker.lastName.toString().trim() !== '';
          const hasNationality = localSpeaker.nationality && localSpeaker.nationality.toString().trim() !== '';
          const hasOrganization = localSpeaker.organization && localSpeaker.organization.toString().trim() !== '';
          const hasPosition = localSpeaker.position && localSpeaker.position.toString().trim() !== '';
          const hasDescription = localSpeaker.description && localSpeaker.description.toString().trim() !== '';
          
          // Validar Biografía: obligatorio PERO puede estar vacío si LinkedIn está lleno
          const hasLinkedin = localSpeaker.linkedin && localSpeaker.linkedin.toString().trim() !== '';
          const hasBiography = localSpeaker.biography && localSpeaker.biography.toString().trim() !== '';
          const hasValidBiography = hasLinkedin || hasBiography; // Biografía es válida si tiene LinkedIn O tiene Biografía

          // Si falta algún campo obligatorio del orador local, retornar false
          // NO se validan thematicAxes ni contentDescription para oradores locales
          if (!hasPhoto || !hasFirstName || !hasLastName || !hasNationality || 
              !hasOrganization || !hasPosition || !hasDescription || !hasValidBiography) {
            return false;
          }
        }
      }
    }

    return true; // Todos los eventos tienen campos completos
  };

  // Función para validar si hay iniciativas con campos obligatorios incompletos
  // SOLO validar campos con asterisco (*) - los marcados como "(opcional)" NO se validan
  const validateInitiatives = (
    initiatives: (Solution | ImpactProgram | PublicPolicy | ChallengesAndNeeds)[] | undefined
  ): boolean => {
    if (!initiatives || initiatives.length === 0) {
      return true; // Si no hay iniciativas, está válido
    }
    
    // Separar por tipo para validar
    const solutions: Solution[] = [];
    const programs: ImpactProgram[] = [];
    const policies: PublicPolicy[] = [];
    const challenges: ChallengesAndNeeds[] = [];
    
    initiatives.forEach(init => {
      const initAny = init as any;
      const isChallenge = !!(initAny.expectedImpact || (initAny.specificNeeds && Array.isArray(initAny.specificNeeds) && initAny.specificNeeds.length > 0));
      
      if (isChallenge) {
        challenges.push(init as ChallengesAndNeeds);
      } else if (init.type === 'program') {
        programs.push(init as ImpactProgram);
      } else if (init.type === 'policy') {
        policies.push(init as PublicPolicy);
      } else {
        solutions.push(init as Solution);
      }
    });
    
    // Validar soluciones
    // Validar soluciones
    if (solutions && solutions.length > 0) {
      for (const solution of solutions) {
        // Solo validar campos obligatorios (con asterisco):
        // - Tipo de Iniciativa (se valida que tenga tipo, pero no se valida aquí porque se valida en el formulario)
        // - Nombre *
        // - Descripción amplia *
        // - Descripción breve *
        // - Ejes temáticos vinculados *
        // - Estado actual (opcional, no se valida)
        const hasName = solution.name && solution.name.toString().trim() !== '';
        const hasShortDescription = solution.shortDescription && solution.shortDescription.toString().trim() !== '';
        const hasDescription = solution.description && solution.description.toString().trim() !== '';
        const hasThematicAxes = solution.thematicAxes && Array.isArray(solution.thematicAxes) && solution.thematicAxes.length > 0;

        if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) {
          return false;
        }

        // NO validar campos opcionales:
        // - solutionType (opcional)
        // - technologiesUsed (opcional)
        // - territory (opcional)

        // Validar campos de contacto si no está marcado "sin contacto"
        // Estos campos tienen asterisco cuando noContact es false
        if (!solution.noContact) {
          const hasContactFirstName = solution.contactFirstName && solution.contactFirstName.trim() !== '';
          const hasContactLastName = solution.contactLastName && solution.contactLastName.trim() !== '';
          const hasContactPosition = solution.contactPosition && solution.contactPosition.trim() !== '';
          const hasContactEmail = solution.contactEmail && solution.contactEmail.trim() !== '';
          if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) {
            return false;
          }
        }
      }
    }

    // Validar programas de impacto
    if (programs && programs.length > 0) {
      for (const program of programs) {
        // Solo validar campos obligatorios (con asterisco):
        const hasName = program.name && program.name.toString().trim() !== '';
        const hasShortDescription = program.shortDescription && program.shortDescription.toString().trim() !== '';
        const hasDescription = program.description && program.description.toString().trim() !== '';
        const hasThematicAxes = program.thematicAxes && Array.isArray(program.thematicAxes) && program.thematicAxes.length > 0;

        if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) {
          return false;
        }

        // NO validar campos opcionales:
        // - estimatedBeneficiaries (opcional)

        // Validar campos de contacto si no está marcado "sin contacto"
        if (!program.noContact) {
          const hasContactFirstName = program.contactFirstName && program.contactFirstName.trim() !== '';
          const hasContactLastName = program.contactLastName && program.contactLastName.trim() !== '';
          const hasContactPosition = program.contactPosition && program.contactPosition.trim() !== '';
          const hasContactEmail = program.contactEmail && program.contactEmail.trim() !== '';
          if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) {
            return false;
          }
        }
      }
    }

    // Validar políticas públicas
    if (policies && policies.length > 0) {
      for (const policy of policies) {
        // Solo validar campos obligatorios (con asterisco):
        const hasName = policy.name && policy.name.toString().trim() !== '';
        const hasShortDescription = policy.shortDescription && policy.shortDescription.toString().trim() !== '';
        const hasDescription = policy.description && policy.description.toString().trim() !== '';
        const hasThematicAxes = policy.thematicAxes && Array.isArray(policy.thematicAxes) && policy.thematicAxes.length > 0;

        if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) {
          return false;
        }

        // NO validar campos opcionales:
        // - policyType (opcional)

        // Validar campos de contacto si no está marcado "sin contacto"
        if (!policy.noContact) {
          const hasContactFirstName = policy.contactFirstName && policy.contactFirstName.trim() !== '';
          const hasContactLastName = policy.contactLastName && policy.contactLastName.trim() !== '';
          const hasContactPosition = policy.contactPosition && policy.contactPosition.trim() !== '';
          const hasContactEmail = policy.contactEmail && policy.contactEmail.trim() !== '';
          if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) {
            return false;
          }
        }
      }
    }

    // Validar desafíos (challenges and needs)
    if (challenges && challenges.length > 0) {
      for (const challenge of challenges) {
        // Solo validar campos obligatorios (con asterisco):
        const hasName = challenge.name && challenge.name.toString().trim() !== '';
        const hasShortDescription = challenge.shortDescription && challenge.shortDescription.toString().trim() !== '';
        const hasDescription = challenge.description && challenge.description.toString().trim() !== '';
        const hasThematicAxes = challenge.thematicAxes && Array.isArray(challenge.thematicAxes) && challenge.thematicAxes.length > 0;

        if (!hasName || !hasShortDescription || !hasDescription || !hasThematicAxes) {
          return false;
        }

        // Validar campos de contacto si no está marcado "sin contacto"
        if (!challenge.noContact) {
          const hasContactFirstName = challenge.contactFirstName && challenge.contactFirstName.trim() !== '';
          const hasContactLastName = challenge.contactLastName && challenge.contactLastName.trim() !== '';
          const hasContactPosition = challenge.contactPosition && challenge.contactPosition.trim() !== '';
          const hasContactEmail = challenge.contactEmail && challenge.contactEmail.trim() !== '';
          if (!hasContactFirstName || !hasContactLastName || !hasContactPosition || !hasContactEmail) {
            return false;
          }
        }
      }
    }

    return true; // Todas las iniciativas tienen campos obligatorios completos
  };

  // Función para validar participantes de rondas de negocios
  const validateBusinessRoundsParticipants = (
    participants: BusinessRoundParticipant[] | undefined
  ): boolean => {
    if (!participants || participants.length === 0) {
      return true; // Si no hay participantes, está válido
    }
    
    for (const participant of participants) {
      // Validar campos obligatorios:
      // - Nombre (firstName) *
      // - Apellido (lastName) *
      // - Cargo (position) *
      // - Organización (organization) *
      // - Correo (email) *
      // - Teléfono (numeroTelefono) *
      // - Ejes temáticos de interés (thematicAxes) *
      const hasFirstName = participant.firstName && participant.firstName.trim() !== '';
      const hasLastName = participant.lastName && participant.lastName.trim() !== '';
      const hasPosition = participant.position && participant.position.trim() !== '';
      const hasOrganization = participant.organization && participant.organization.trim() !== '';
      const hasEmail = participant.email && participant.email.trim() !== '';
      const hasPhone = participant.numeroTelefono && participant.numeroTelefono.trim() !== '';
      const hasThematicAxes = participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0;

      if (!hasFirstName || !hasLastName || !hasPosition || !hasOrganization || !hasEmail || !hasPhone || !hasThematicAxes) {
        return false;
      }
    }
    
    return true; // Todos los participantes tienen campos obligatorios completos
  };

  // Función para identificar qué pestañas tienen campos incompletos
  const getIncompleteTabs = (data: ProfileData): string[] => {
    const tabs: string[] = [];
    
    // Validar pestaña de oradores
    if (!validateSpeakers(data.speakers)) {
      tabs.push('Oradores en congreso');
    }
    
    // Validar pestaña de eventos paralelos
    if (!validateParallelEvents(data.parallelEvents)) {
      tabs.push('Eventos paralelos');
    }
    
    // Validar pestaña de iniciativas
    if (!validateInitiatives(data.initiatives)) {
      tabs.push('Catálogo / Rondas de negocios');
    }
    
    // Validar participantes de rondas de negocios
    if (!validateBusinessRoundsParticipants(data.businessRoundsParticipants)) {
      // Solo agregar si no está ya en la lista (porque están en la misma pestaña)
      if (!tabs.includes('Catálogo / Rondas de negocios')) {
        tabs.push('Catálogo / Rondas de negocios');
      }
    }
    
    return tabs;
  };

  // Función para guardar el perfil (extraída para poder llamarla desde el modal)
  const performSave = async (data: ProfileData) => {
    setLoading(true);
    setErrorMessage('');
    
    try {
      // Validar datos antes de guardar
      const errors = validateProfile(data);
      
      if (errors.length > 0) {
        // Hay errores de validación, mostrar modal
        setValidationErrors(errors);
        setPendingSaveData(data);
        setShowValidationErrorsModal(true);
        setLoading(false);
        return;
      }
      
      // Subir logo a WordPress si es necesario
      let dataToSave = { ...data };
      if (data.basicInfo.logo && typeof data.basicInfo.logo !== 'string') {
        // Si el logo es un File, subirlo a WordPress
        const logoFile = data.basicInfo.logo as File;
        const logoUrl = await ensureSupabaseUrl(logoFile, {
          folder: 'perfil-institucional/logos'
        });
        
        dataToSave = {
          ...dataToSave,
          basicInfo: {
            ...dataToSave.basicInfo,
            logo: logoUrl
          }
        };
      }

      // Los archivos ya deberían estar en WordPress (se suben inmediatamente al seleccionarlos)
      // Solo manejar casos donde todavía hay File objects (fallback)
      if (dataToSave.speakers && dataToSave.speakers.length > 0) {
        const speakersWithWordPressFiles = await Promise.all(
          dataToSave.speakers.map(async (speaker) => {
            let updatedSpeaker = { ...speaker };
            
            // Solo subir si todavía es un File (fallback)
            if (speaker.photo && typeof speaker.photo !== 'string') {
              const photoFile = speaker.photo as File;
              const photoUrl = await ensureSupabaseUrl(photoFile, {
                folder: 'perfil-institucional/speakers/photos'
              });
              updatedSpeaker.photo = photoUrl;
            }
            
            // Filtrar documentos con base64 antiguo y mantener solo URLs
            if (speaker.supportDocuments && Array.isArray(speaker.supportDocuments)) {
              const filteredDocs = speaker.supportDocuments.filter((doc) => {
                if (doc instanceof File) {
                  return false; // Omitir Files que no se subieron
                }
                if (typeof doc === 'string' && doc.includes('FILENAME:') && doc.includes(':DATA:')) {
                  // Omitir documentos con base64 antiguo
                  return false;
                }
                return true; // Mantener URLs de WordPress
              });
              updatedSpeaker.supportDocuments = filteredDocs.length > 0 ? filteredDocs : undefined;
            }
            
            return updatedSpeaker;
          })
        );
        
        dataToSave = {
          ...dataToSave,
          speakers: speakersWithWordPressFiles
        };
      }

      // Los archivos ya deberían estar en WordPress (se suben inmediatamente al seleccionarlos)
      // Solo filtrar documentos con base64 antiguo y manejar casos excepcionales
      if (dataToSave.parallelEvents && dataToSave.parallelEvents.length > 0) {
        const eventsCleaned = dataToSave.parallelEvents.map((event) => {
          let updatedEvent = { ...event };
          
          // Filtrar archivos con base64 antiguo
          if (event.presentationFile && Array.isArray(event.presentationFile)) {
            updatedEvent.presentationFile = event.presentationFile.filter((file) => {
              if (typeof file === 'string' && file.includes('FILENAME:') && file.includes(':DATA:')) {
                return false; // Omitir base64 antiguo
              }
              if (file instanceof File) {
                return false; // Omitir Files que no se subieron
              }
              return true; // Mantener URLs de WordPress
            });
            if (updatedEvent.presentationFile.length === 0) {
              updatedEvent.presentationFile = undefined;
            }
          }
          
          // Manejar fotos de localSpeakers (fallback si todavía es File)
          if (event.localSpeakers && Array.isArray(event.localSpeakers) && event.localSpeakers.length > 0) {
            updatedEvent.localSpeakers = event.localSpeakers.map((localSpeaker) => {
              // Si todavía es File, intentar subirlo (fallback)
              if (localSpeaker.photo && typeof localSpeaker.photo !== 'string') {
                // No subir aquí, solo omitir
                return { ...localSpeaker, photo: undefined };
              }
              
              // Filtrar documentos con base64 antiguo
              if (localSpeaker.supportDocuments && Array.isArray(localSpeaker.supportDocuments)) {
                const filteredDocs = localSpeaker.supportDocuments.filter((doc) => {
                  if (doc instanceof File) {
                    return false;
                  }
                  if (typeof doc === 'string' && doc.includes('FILENAME:') && doc.includes(':DATA:')) {
                    return false; // Omitir base64 antiguo
                  }
                  return true; // Mantener URLs de WordPress
                });
                return {
                  ...localSpeaker,
                  supportDocuments: filteredDocs.length > 0 ? filteredDocs : undefined
                };
              }
              return localSpeaker;
            });
          }
          
          return updatedEvent;
        });
        
        dataToSave = {
          ...dataToSave,
          parallelEvents: eventsCleaned
        };
      }

      // Los archivos ya deberían estar en WordPress (se suben inmediatamente al seleccionarlos)
      // Solo filtrar documentos con base64 antiguo y Files que no se subieron
      const filterDocuments = (docs: any[]): string[] | undefined => {
        const filtered = docs.filter((doc) => {
          if (doc instanceof File) {
            return false; // Omitir Files que no se subieron
          }
          if (typeof doc === 'string' && doc.includes('FILENAME:') && doc.includes(':DATA:')) {
            return false; // Omitir base64 antiguo
          }
          return true; // Mantener URLs de WordPress
        });
        return filtered.length > 0 ? filtered as string[] : undefined;
      };

      // Filtrar documentos de iniciativas (si existen)
      if ((dataToSave as any).iniciativas && Array.isArray((dataToSave as any).iniciativas) && (dataToSave as any).iniciativas.length > 0) {
        (dataToSave as any).iniciativas = (dataToSave as any).iniciativas.map((initiative: any) => {
          if (initiative.supportDocuments && Array.isArray(initiative.supportDocuments)) {
            return {
              ...initiative,
              supportDocuments: filterDocuments(initiative.supportDocuments)
            };
          }
          return initiative;
        });
      }

      // Función para limpiar objetos File y datos grandes antes de enviar
      const cleanPayload = (obj: any, path: string = ''): any => {
        if (obj === null || obj === undefined) {
          return obj;
        }
        
        // Si es un File object, retornar null (no debería llegar aquí, pero por si acaso)
        if (obj instanceof File) {
          return null;
        }
        
        // Si es un array, limpiar cada elemento
        if (Array.isArray(obj)) {
          return obj.map((item, index) => cleanPayload(item, `${path}[${index}]`)).filter(item => item !== null);
        }
        
        // Si es un objeto, limpiar cada propiedad
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const value = cleanPayload(obj[key], path ? `${path}.${key}` : key);
              // Solo incluir si no es null (a menos que sea intencional)
              // EXCEPCIÓN: Si es basicInfo.logo y es null, siempre incluirlo para poder eliminar el logo
              const isLogoField = (path === 'basicInfo' || (path && path.endsWith('.basicInfo'))) && key === 'logo';
              if (value !== null || obj[key] === null || (isLogoField && value === null)) {
                cleaned[key] = value;
              }
            }
          }
          return cleaned;
        }
        
        // Para strings, verificar si contienen base64 grande
        if (typeof obj === 'string') {
          // EXCEPCIÓN: No filtrar si es basicInfo.logo (puede ser data URL temporal o URL de Supabase)
          const isLogoField = path === 'basicInfo' || (path && path.endsWith('.basicInfo'));
          if (isLogoField && path.includes('logo')) {
            // Permitir todas las URLs de logo (data URLs, URLs de Supabase, etc.)
            return obj;
          }
          
          // Si es un string muy grande (más de 100KB), podría ser base64
          if (obj.length > 100000 && obj.includes('data:') && obj.includes('base64')) {
            return null;
          }
          // Si es formato antiguo con base64, omitir
          if (obj.includes('FILENAME:') && obj.includes(':DATA:')) {
            return null;
          }
        }
        
        return obj;
      };
      
      // Asegurar que si logo es undefined, se convierta a null ANTES de limpiar el payload
      // Esto es necesario para eliminar el logo de la BD cuando el usuario lo elimina
      const logoWasDeleted = dataToSave.basicInfo && (dataToSave.basicInfo.logo === undefined || dataToSave.basicInfo.logo === null);
      
      if (dataToSave.basicInfo && logoWasDeleted) {
        // Usar 'as any' para permitir null temporalmente, será convertido a null en cleanedData
        (dataToSave.basicInfo as any).logo = null;
      }
      
      // Limpiar el payload antes de enviar
      const cleanedData = cleanPayload(dataToSave);
      
      // Asegurar que basicInfo existe después de limpiar
      if (!cleanedData.basicInfo) {
        cleanedData.basicInfo = {};
      }
      
      // Asegurar que logo siempre esté presente como null si fue eliminado
      // Esto garantiza que se envíe al backend para actualizar la BD
      if (logoWasDeleted) {
        cleanedData.basicInfo.logo = null;
      }
      
      // Calcular progreso y agregar campos de validación
      const wantsToManageProfile = cleanedData.contactInfo?.wantsToManageProfile || false;
      
      // Calcular progreso del perfil personal
      const personalProgress = calculateProfileProgress(cleanedData, false);
      cleanedData.perfilPersonalValidado = personalProgress.isComplete;
      
      // Calcular progreso del perfil institucional (solo si wantsToManageProfile es true)
      if (wantsToManageProfile) {
        const institutionalProgress = calculateProfileProgress(cleanedData, true);
        cleanedData.perfilInstitucionalValidado = institutionalProgress.isComplete;
      } else {
        cleanedData.perfilInstitucionalValidado = false;
      }
      
      // Validar tamaño del payload antes de enviar (límite de Vercel: ~4.5MB, pero más seguro usar 3.5MB)
      let payloadString: string;
      try {
        payloadString = JSON.stringify(cleanedData);
      } catch (error) {
        setErrorMessage('Error al preparar los datos para guardar. Por favor, intenta nuevamente.');
        setLoading(false);
        return;
      }
      
      const payloadSizeMB = new Blob([payloadString]).size / (1024 * 1024);
      
      // Analizar qué está ocupando más espacio
      if (payloadSizeMB > 1) {
      }
      
      // Si el payload es demasiado grande, guardar por secciones
      if (payloadSizeMB > 3.5) {
        
        try {
          // Guardar secciones básicas primero (siempre necesarias)
          if (cleanedData.basicInfo) {
            // Asegurar que logo esté presente como null si fue eliminado
            const basicInfoToSave = { ...cleanedData.basicInfo };
            if (logoWasDeleted) {
              (basicInfoToSave as any).logo = null;
            }
            const basicInfoResponse = await saveProfileSection('basicInfo', basicInfoToSave);
            if (!basicInfoResponse.success) {
              throw new Error(basicInfoResponse.error || 'Error al guardar información básica');
            }
          }
          
          if (cleanedData.contactInfo) {
            const contactInfoResponse = await saveProfileSection('contactInfo', cleanedData.contactInfo);
            if (!contactInfoResponse.success) {
              throw new Error(contactInfoResponse.error || 'Error al guardar información de contacto');
            }
          }
          
          if (cleanedData.institutionalDescription) {
            const descResponse = await saveProfileSection('institutionalDescription', cleanedData.institutionalDescription);
            if (!descResponse.success) {
              throw new Error(descResponse.error || 'Error al guardar descripción institucional');
            }
          }
          
          if (cleanedData.catalogSections) {
            const catalogResponse = await saveProfileSection('catalogSections', cleanedData.catalogSections);
            if (!catalogResponse.success) {
              throw new Error(catalogResponse.error || 'Error al guardar secciones del catálogo');
            }
          }
          
          // Guardar todas las iniciativas de una vez
          const initiativesResponse = await saveProfileSection('iniciativas', cleanedData.initiatives || []);
          if (!initiativesResponse.success) {
            throw new Error(initiativesResponse.error || 'Error al guardar iniciativas');
          }
          
          if (cleanedData.speakers && cleanedData.speakers.length > 0) {
            const speakersResponse = await saveProfileSection('speakers', cleanedData.speakers);
            if (!speakersResponse.success) {
              throw new Error(speakersResponse.error || 'Error al guardar oradores');
            }
          }
          
          if (cleanedData.parallelEvents && cleanedData.parallelEvents.length > 0) {
            const eventsResponse = await saveProfileSection('parallelEvents', cleanedData.parallelEvents);
            if (!eventsResponse.success) {
              throw new Error(eventsResponse.error || 'Error al guardar eventos paralelos');
            }
          }
          
          if (cleanedData.participationModalities) {
            const modalitiesResponse = await saveProfileSection('participationModalities', {
              participationModalities: cleanedData.participationModalities,
              agreedModality: cleanedData.agreedModality,
              modalityObservations: cleanedData.modalityObservations,
              requestInformation: cleanedData.requestInformation
            });
            if (!modalitiesResponse.success) {
              throw new Error(modalitiesResponse.error || 'Error al guardar modalidades de participación');
            }
          }
          
          if (cleanedData.selectedStandsUpdated || cleanedData.selectedStands || cleanedData.confirmedStands) {
            const standsResponse = await saveProfileSection('stands', {
              selectedStands: cleanedData.selectedStands, // Mantener para compatibilidad
              selectedStandsUpdated: cleanedData.selectedStandsUpdated, // Campo nuevo con nombres actualizados
              confirmedStands: cleanedData.confirmedStands,
              requestExpoInformation: cleanedData.requestExpoInformation
            });
            if (!standsResponse.success) {
              throw new Error(standsResponse.error || 'Error al guardar stands');
            }
          }
          
          // Simular respuesta exitosa
          const response = { success: true };
          
          if (response.success) {
            // CRÍTICO: Actualizar initialProfileData con los datos actuales del formulario
            // Esto asegura que checkForUnsavedChanges funcione correctamente después de guardar
            if (getFormDataRef.current) {
              const currentFormData = getFormDataRef.current();
              setInitialProfileData(currentFormData);
            } else {
              setInitialProfileData(cleanedData as ProfileData);
            }
            // No llamar loadProfile aquí: sobrescribe initialProfileData con la API y el merge en ProfileForm
            // vacía temporalmente teléfono, URLs, logo del sidebar (pestañeo) hasta la siguiente recarga.
            
            // Mostrar modal de éxito
            showSuccess('El perfil se ha guardado correctamente.', 'Perfil guardado', true);
          }
        } catch (sectionError: any) {
          // Verificar si es un error de timeout
          if (sectionError?.isTimeout || (sectionError?.response && sectionError.response.isTimeout)) {
            showTimeout('guardar el perfil', () => performSave(data));
            setLoading(false);
            return;
          }
          // Verificar si es un error de rate limiting (429)
          if (handleRateLimitError(sectionError)) {
            setLoading(false);
            return;
          }
          // Verificar si es un error 401 (sesión expirada)
          // CRÍTICO: NO mostrar modal si acabamos de hacer logout
          const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
          const justLoggedOut = logoutTimestamp && (Date.now() - parseInt(logoutTimestamp)) < 3000;
          if ((sectionError?.status === 401 || sectionError?.statusCode === 401) && !justLoggedOut) {
            showSessionExpired();
            setLoading(false);
            return;
          }
          // Verificar si es un error del servidor (5xx)
          const status = sectionError?.status || sectionError?.statusCode;
          if (status === 500 || status === 502 || status === 503 || status === 504) {
            showServerError(sectionError?.message || 'Error del servidor', status);
            setLoading(false);
            return;
          }
          // Mostrar modal de error de guardado
          showSaveError(sectionError.message || 'Error al guardar el perfil por secciones');
        } finally {
          setLoading(false);
        }
        
        return;
      }
      
      // Si el payload es pequeño, guardar normalmente
      // Asegurar que logo esté presente como null si fue eliminado (también para saveProfile completo)
      if (logoWasDeleted && cleanedData.basicInfo) {
        cleanedData.basicInfo.logo = null;
      }
      
      // Log para debugging: verificar que el logo se está enviando
      
      const response = await saveProfile(cleanedData);
      
      if (response.success) {
        // Calcular progreso para tracking
        const wantsToManageProfile = cleanedData.contactInfo?.wantsToManageProfile || false;
        const progress = calculateProfileProgress(cleanedData, wantsToManageProfile);
        
        // Track guardado exitoso
        AnalyticsEvents.profileSave(
          cleanedData.isDraft || false,
          progress.percentage,
          false
        );

        // CRÍTICO: Actualizar initialProfileData con los datos actuales del formulario
        // Esto asegura que checkForUnsavedChanges funcione correctamente después de guardar
        // Usar getFormDataRef para obtener los datos exactos del formulario, no cleanedData
        if (getFormDataRef.current) {
          const currentFormData = getFormDataRef.current();
          setInitialProfileData({
            ...currentFormData,
            basicInfo: {
              ...currentFormData.basicInfo,
              logo: currentFormData.basicInfo?.logo
            }
          });
        } else {
          setInitialProfileData({
            ...cleanedData,
            basicInfo: {
              ...cleanedData.basicInfo,
              logo: cleanedData.basicInfo?.logo
            }
          } as ProfileData);
        }
        // Sin loadProfile post-guardado: evita pestañeo y borrado de teléfono, URLs y logo del sidebar
        
        // Mostrar modal de éxito
        showSuccess('El perfil se ha guardado correctamente.', 'Perfil guardado', true);
      } else {
        // Verificar si es un error de timeout
        if ((response as any).isTimeout) {
          showTimeout('guardar el perfil', () => performSave(data));
          setLoading(false);
          return;
        }
        // Verificar si es un error 401 (sesión expirada)
        // CRÍTICO: NO mostrar modal si acabamos de hacer logout O no hay usuario
        const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
        const justLoggedOut = logoutTimestamp && (Date.now() - parseInt(logoutTimestamp)) < 10000;
        const user = localStorage.getItem('user');
        if (response.error && (response as any).status === 401 && !justLoggedOut && user) {
          showSessionExpired();
          setLoading(false);
          return;
        }
        // Verificar si es un error del servidor (5xx)
        const status = (response as any).status;
        if (status === 500 || status === 502 || status === 503 || status === 504) {
          showServerError(response.error || 'Error del servidor', status);
          setLoading(false);
          return;
        }
        // Mostrar modal de error de guardado
        showSaveError(response.error || 'Error al guardar el perfil');
      }
    } catch (error: any) {
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout('guardar el perfil', () => performSave(data));
        setLoading(false);
        return;
      }
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        setLoading(false);
        return;
      }
      // Verificar si es un error 401 (sesión expirada)
      // CRÍTICO: NO mostrar modal si acabamos de hacer logout O no hay usuario
      const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
      const justLoggedOut = logoutTimestamp && (Date.now() - parseInt(logoutTimestamp)) < 10000;
      const user = localStorage.getItem('user');
      if ((error?.status === 401 || error?.statusCode === 401) && !justLoggedOut && user) {
        showSessionExpired();
        setLoading(false);
        return;
      }
      // Verificar si es un error del servidor (5xx)
      const status = error?.status || error?.statusCode;
      if (status === 500 || status === 502 || status === 503 || status === 504) {
        showServerError(error?.message || 'Error del servidor', status);
        setLoading(false);
        return;
      }
      // Mostrar modal de error de guardado
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      if (errorMsg.includes('WordPress') || errorMsg.includes('wordpress')) {
        showSaveError(`Error al subir archivos: ${errorMsg}`);
      } else {
        showSaveError(`Error de conexión: ${errorMsg}. Por favor, intenta nuevamente.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para vaciar campos con errores y guardar
  const handleClearErrorsAndSave = async () => {
    if (!pendingSaveData) return;
    
    setShowValidationErrorsModal(false);
    setLoading(true);
    
    try {
      // Crear copia profunda de los datos
      const cleanedData = JSON.parse(JSON.stringify(pendingSaveData)) as ProfileData;
      
      // Vaciar campos con errores
      validationErrors.forEach(error => {
        // PERFIL PERSONAL
        if (error.tab === 'Información Personal') {
          if (error.field === 'numeroTelefono' && cleanedData.contactInfo) {
            // Vaciar numeroTelefono
            cleanedData.contactInfo.numeroTelefono = '';
            // CRÍTICO: También limpiar el campo phone completamente
            // Si el valor estaba solo en phone (sin numeroTelefono), necesitamos vaciarlo también
            const codigoPais = cleanedData.contactInfo.codigoPais || 'AR';
            cleanedData.contactInfo.phone = getDialCode(codigoPais);
          }
          if (error.field === 'website' && cleanedData.basicInfo) {
            cleanedData.basicInfo.website = '';
          }
        }
        // INFO DE LA ORG
        else if (error.tab === 'Información de la Organización' && cleanedData.basicInfo?.socialNetworks) {
          if (error.field === 'facebook') {
            cleanedData.basicInfo.socialNetworks.facebook = '';
          }
          if (error.field === 'twitter') {
            cleanedData.basicInfo.socialNetworks.twitter = '';
          }
          if (error.field === 'linkedin') {
            cleanedData.basicInfo.socialNetworks.linkedin = '';
          }
          if (error.field === 'instagram') {
            cleanedData.basicInfo.socialNetworks.instagram = '';
          }
          if (error.field === 'youtube') {
            cleanedData.basicInfo.socialNetworks.youtube = '';
          }
          if (error.field === 'other') {
            cleanedData.basicInfo.socialNetworks.other = '';
          }
        }
        // ORADORES
        else if (error.tab === 'Oradores' && cleanedData.speakers) {
          const speakerIndex = error.subTab ? parseInt(error.subTab.replace('Orador ', '').replace(/\s/g, '')) - 1 : -1;
          if (speakerIndex >= 0 && cleanedData.speakers[speakerIndex]) {
            if (error.field === 'biography') {
              cleanedData.speakers[speakerIndex].biography = '';
            }
            if (error.field === 'linkedin') {
              cleanedData.speakers[speakerIndex].linkedin = '';
            }
            if (error.field === 'instagram') {
              cleanedData.speakers[speakerIndex].instagram = '';
            }
            if (error.field === 'twitter') {
              cleanedData.speakers[speakerIndex].twitter = '';
            }
          }
        }
        // ORADORES DENTRO DE EVENTOS PARALELOS
        else if (error.tab === 'Eventos Paralelos' && cleanedData.parallelEvents) {
          const match = error.subTab?.match(/Evento (\d+) - Orador (\d+)/);
          if (match) {
            const eventIndex = parseInt(match[1]) - 1;
            const speakerIndex = parseInt(match[2]) - 1;
            if (eventIndex >= 0 && speakerIndex >= 0 && 
                cleanedData.parallelEvents[eventIndex]?.localSpeakers?.[speakerIndex]) {
              const speaker = cleanedData.parallelEvents[eventIndex].localSpeakers![speakerIndex];
              if (error.field === 'biography') {
                speaker.biography = '';
              }
              if (error.field === 'linkedin') {
                speaker.linkedin = '';
              }
              if (error.field === 'instagram') {
                speaker.instagram = '';
              }
              if (error.field === 'twitter') {
                speaker.twitter = '';
              }
            }
          }
        }
        // INICIATIVAS
        else if (error.tab === 'Iniciativas' && cleanedData.initiatives) {
          const match = error.subTab?.match(/Iniciativa (\d+)/);
          if (match) {
            const initiativeIndex = parseInt(match[1]) - 1;
            if (initiativeIndex >= 0 && cleanedData.initiatives[initiativeIndex]) {
              if (error.field === 'contactEmail') {
                cleanedData.initiatives[initiativeIndex].contactEmail = '';
              }
              if (error.field === 'contactNumeroTelefono') {
                cleanedData.initiatives[initiativeIndex].contactNumeroTelefono = '';
                // También limpiar el campo contactPhone que se deriva de contactNumeroTelefono
                const codigoPais = cleanedData.initiatives[initiativeIndex].contactCodigoPais || 'AR';
                cleanedData.initiatives[initiativeIndex].contactPhone = getDialCode(codigoPais);
              }
            }
          }
        }
        // PARTICIPANTES DE LAS RONDAS
        else if (error.tab === 'Participantes en las Rondas de Negocios' && cleanedData.businessRoundsParticipants) {
          const match = error.subTab?.match(/Participante (\d+)/);
          if (match) {
            const participantIndex = parseInt(match[1]) - 1;
            if (participantIndex >= 0 && cleanedData.businessRoundsParticipants[participantIndex]) {
              if (error.field === 'email') {
                cleanedData.businessRoundsParticipants[participantIndex].email = '';
              }
              if (error.field === 'numeroTelefono') {
                cleanedData.businessRoundsParticipants[participantIndex].numeroTelefono = '';
                // También limpiar el campo phone que se deriva de numeroTelefono
                const codigoPais = cleanedData.businessRoundsParticipants[participantIndex].codigoPais || 'AR';
                cleanedData.businessRoundsParticipants[participantIndex].phone = getDialCode(codigoPais);
              }
            }
          }
        }
      });
      
      // CRÍTICO: Actualizar initialProfileData INMEDIATAMENTE para que el formulario se actualice en el frontend
      // Esto hace que los campos se vacíen visualmente y los errores desaparezcan
      setInitialProfileData(cleanedData);
      
      // CRÍTICO: Limpiar errores de validación INMEDIATAMENTE para que desaparezcan las leyendas
      setValidationErrors([]);
      
      // DESPUÉS DE VACIAR CAMPOS: Validar campos obligatorios
      const incompleteTabsList = getIncompleteTabs(cleanedData);
      
      if (incompleteTabsList.length > 0) {
        // Hay campos incompletos después de vaciar, mostrar modal con las pestañas afectadas
        setPendingFormData(cleanedData);
        setIncompleteTabs(incompleteTabsList);
        setShowSpeakerValidationModal(true);
        // Limpiar estados del modal de errores
        setPendingSaveData(null);
        setValidationErrors([]);
        setLoading(false);
        // Preservar acciones pendientes (logout/navegación) si existen
        // Estas se manejarán cuando el usuario confirme guardar desde el modal de campos obligatorios
        return;
      }
      
      // CRÍTICO: Si no hay campos incompletos, mostrar modal de borrador antes de guardar
      // Esto mantiene el flujo correcto: validar errores → validar campos obligatorios → modal de borrador → guardar
      setPendingSaveData(cleanedData);
      setShowDraftModal(true);
      setLoading(false);
      
      // Limpiar solo los errores de validación (pendingSaveData se mantiene para el modal de borrador)
      setValidationErrors([]);
      
      // NOTA: Las acciones pendientes (logout/navegación) se manejarán cuando el usuario
      // confirme guardar desde el modal de borrador, no aquí
    } catch (error: any) {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setErrorMessage(`Error al guardar: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Función principal que maneja el submit del formulario
  const handleFormSubmit = async (data: ProfileData) => {
    // PRIMERO: Validar errores de formato (URLs, emails, teléfonos incorrectos)
    const errors = validateProfile(data);
    
    if (errors.length > 0) {
      // Hay errores de validación, mostrar modal de campos incorrectos
      setValidationErrors(errors);
      setPendingSaveData(data);
      setShowValidationErrorsModal(true);
      // Track errores de validación
      errors.forEach(error => {
        AnalyticsEvents.validationError(error.field, error.label, error.tab);
      });
      return;
    }

    // SEGUNDO: Si no hay errores de formato, validar campos obligatorios
    const incompleteTabsList = getIncompleteTabs(data);
    
    if (incompleteTabsList.length > 0) {
      // Hay campos incompletos, mostrar modal con las pestañas afectadas
      setPendingFormData(data);
      setIncompleteTabs(incompleteTabsList);
      setShowSpeakerValidationModal(true);
      return;
    }

    // Si no hay errores ni campos incompletos, mostrar modal para elegir entre borrador o guardado definitivo
    setPendingSaveData(data);
    setShowDraftModal(true);
  };

  // Función para manejar la confirmación del modal de cambios sin guardar
  const handleConfirmSaveBeforeExit = async () => {
    setShowUnsavedChangesModal(false);
    setUnsavedModalContext(null);

    if (!getFormDataRef.current) {
      // Si no hay datos, continuar con la acción pendiente
      if (pendingLogout) {
        setPendingLogout(false);
        await performLogout();
      } else if (pendingNavigation) {
        const navFn = pendingNavigation;
        setPendingNavigation(null);
        navFn();
      }
      return;
    }

    const currentData = getFormDataRef.current();

    // PRIMERO: Validar errores de formato (URLs, emails, teléfonos incorrectos)
    const errors = validateProfile(currentData);
    
    if (errors.length > 0) {
      // Hay errores de validación, mostrar modal de campos incorrectos
      setValidationErrors(errors);
      setPendingSaveData(currentData);
      setShowValidationErrorsModal(true);
      // Guardar la acción pendiente para ejecutarla después del guardado
      if (pendingLogout) {
        setPendingLogout(true);
      }
      return;
    }

    // SEGUNDO: Si no hay errores de formato, validar campos obligatorios
    const incompleteTabsList = getIncompleteTabs(currentData);

    if (incompleteTabsList.length > 0) {
      // Hay campos incompletos, mostrar modal con las pestañas afectadas
      setPendingFormData(currentData);
      setIncompleteTabs(incompleteTabsList);
      setShowSpeakerValidationModal(true);
      // Guardar la acción pendiente para ejecutarla después del guardado
      if (pendingLogout) {
        setPendingLogout(true);
      }
      return;
    }

    // Si no hay errores ni campos incompletos, mostrar modal para elegir entre borrador o guardado definitivo
    setPendingSaveData(currentData);
    setShowDraftModal(true);
    // Guardar la acción pendiente para ejecutarla después del guardado
    if (pendingLogout) {
      setPendingLogout(true);
    }
  };

  // Función para cancelar la salida
  const handleCancelExit = () => {
    setShowUnsavedChangesModal(false);
    setPendingNavigation(null);
    setPendingLogout(false);
    setUnsavedModalContext(null);
  };

  // Función para salir sin guardar
  const handleExitWithoutSaving = () => {
    setShowUnsavedChangesModal(false);
    setUnsavedModalContext(null);

    if (pendingLogout) {
      setPendingLogout(false);
      performLogout();
    } else if (pendingNavigation) {
      const navFn = pendingNavigation;
      setPendingNavigation(null);
      navFn();
    }
  };

  // Función para manejar la confirmación del modal (guardar de todas formas)
  const handleConfirmSave = async () => {
    setShowSpeakerValidationModal(false);
    if (pendingFormData) {
      // En lugar de guardar directamente, mostrar el modal de autorización
      // Guardar los datos pendientes para guardarlos después de la autorización
      setPendingSaveData(pendingFormData);
      setPendingFormData(null);
      setIncompleteTabs([]);
      
      // Mostrar modal para elegir entre borrador o guardado definitivo
      setPendingSaveData(pendingFormData);
      setShowDraftModal(true);
    }
  };

  // Función para manejar la elección de borrador o guardado definitivo
  const handleDraftChoice = async (isDraft: boolean) => {
    
    // Cerrar el modal inmediatamente para dar respuesta visual al usuario
    setShowDraftModal(false);
    setSavingDraft(true);
    
    try {
      // Guardar el estado del perfil (true = borrador, false = guardado definitivo)
      const response = await updateDraftStatus(isDraft);
      
      if (response.success) {
        // Actualizar el perfil local
        if (initialProfileData) {
          setInitialProfileData({
            ...initialProfileData,
            isDraft: isDraft
          });
        }
        
        // Después de guardar el estado, guardar el perfil si hay datos pendientes
        if (pendingSaveData) {
          try {
            // Actualizar los datos pendientes con el estado de borrador
            const dataWithDraftStatus = {
              ...pendingSaveData,
              isDraft: isDraft
            };
            // Guardar el perfil
            await performSave(dataWithDraftStatus);
            // Actualizar initialProfileData para que no se detecten cambios sin guardar
            // Preservar el logo actual antes de actualizar
            if (getFormDataRef.current) {
              const currentFormData = getFormDataRef.current();
              const currentLogo = currentFormData.basicInfo?.logo;
              setInitialProfileData(currentFormData);
              
              // Si hay un logo actual, asegurarse de que se preserve después de cualquier recarga
              if (currentLogo && (typeof currentLogo === 'string' && currentLogo.trim() !== '')) {
                // El logo ya está en currentFormData, así que se preservará automáticamente
              }
            }
            // Limpiar los datos pendientes
            setPendingSaveData(null);

            // Si había una acción pendiente (logout o navegación), ejecutarla
            if (pendingLogout) {
              setPendingLogout(false);
              await performLogout();
            } else if (pendingNavigation) {
              const navFn = pendingNavigation;
              setPendingNavigation(null);
              setUnsavedModalContext(null);
              navFn();
            }
          } catch (saveError) {
            // No limpiar pendingSaveData si hay error, para que pueda intentar de nuevo
          }
        }
      } else {
        setErrorMessage(response.error || 'Error al guardar el estado del perfil');
      }
    } catch (error) {
      setErrorMessage('Error al guardar el estado del perfil. Por favor, intenta nuevamente.');
    } finally {
      setSavingDraft(false);
    }
  };

  // Función para manejar la descarga del PDF
  const handleDownloadPDF = async () => {
    try {
      setIsDownloadingPDF(true);
      setPdfProgress(0);
      
      const currentGetFormData = getFormDataRef.current;
      const formData = currentGetFormData && typeof currentGetFormData === 'function' 
        ? currentGetFormData() 
        : initialProfileData;
      
      if (formData) {
        await generateProfilePDF(formData, (progress) => {
          setPdfProgress(progress);
        });
      }
    } catch (error) {
      // Error silencioso, solo log
    } finally {
      setIsDownloadingPDF(false);
      setPdfProgress(0);
    }
  };


  if (checkingAuth) {
    return (
      <div className="app">
        <div className="loading-overlay">
          <div className="loading-spinner">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <AuthWrapper onAuthSuccess={handleAuthSuccess} />
      </div>
    );
  }

  return (
    <div className="app app-main-page">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">Guardando...</div>
        </div>
      )}
      {isUploading && (
        <div className="loading-overlay">
          <div className="upload-progress-spinner">
            <div className="upload-progress-text">
              {uploadFileCount > 1 
                ? `Subiendo archivos (${currentFileIndex + 1}/${uploadFileCount})...`
                : 'Subiendo archivo...'}
            </div>
            {uploadFileName && (
              <div className="upload-file-name">{uploadFileName}</div>
            )}
            <div className="upload-progress-bar-container">
              <div 
                className="upload-progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className="upload-progress-percentage">{Math.round(uploadProgress)}%</div>
          </div>
        </div>
      )}
      {isDownloadingPDF && (
        <div className="loading-overlay">
          <div className="upload-progress-spinner">
            <div className="upload-progress-text">
              Descargando PDF...
            </div>
            <div className="upload-progress-bar-container">
              <div 
                className="upload-progress-bar" 
                style={{ width: `${pdfProgress}%` }}
              ></div>
            </div>
            <div className="upload-progress-percentage">{Math.round(pdfProgress)}%</div>
          </div>
        </div>
      )}
      <ConnectionErrorBanner />
      {errorMessage && (
        <div className="error-message-global">
          {errorMessage}
        </div>
      )}
      {isAuthenticated && (userRole === 'admin' || userRole === 'editor') ? (
        <AdminPanel />
      ) : (
        <>
          <div className="app-miniheader">
            <div className="miniheader-left">
              <a 
                href="/terminos-y-condiciones" 
                className="miniheader-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Términos y Condiciones
              </a>
            </div>
            <div className="miniheader-social">
              <a 
                href="https://smartcityexposantiagodelestero.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="miniheader-link"
              >
                smartcityexposantiagodelestero.com
              </a>
            </div>
          </div>
          <div className="app-header-bar">
            <div className="header-logo">
              <a 
                href="/" 
                className="header-logo-link"
              >
                <ProxyImage 
                  src={isDarkMode 
                    ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                    : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
                  } 
                  alt="Smart City Expo Santiago del Estero" 
                  className="header-logo-image"
                />
              </a>
            </div>
            {/* Logo Mood para mobile - fuera de header-actions */}
            <MoodLogoWithTooltip
              isDarkMode={isDarkMode}
              className="header-mood-logo header-mood-logo-mobile"
              />
            <div className="header-actions">
              {isAuthenticated && userRole !== 'admin' && userRole !== 'editor' && (
                <>
                  <button 
                    type="button"
                    className="save-profile-button"
                    onClick={async () => {
                      // Usar el ref directamente que siempre tiene la función actual
                      const currentGetFormData = getFormDataRef.current;
                      if (currentGetFormData && typeof currentGetFormData === 'function') {
                        try {
                          const formData = currentGetFormData();
                          await handleFormSubmit(formData);
                        } catch (error) {
                        }
                      } else {
                      }
                    }}
                    disabled={loading || !isFormDataReady}
                  >
                    {loading ? 'Guardando...' : 'Guardar Perfil'}
                  </button>
                  <ThemeToggle />
                  {currentWantsToManageProfile && (
                  <ShareDropdown
                    profileData={initialProfileData}
                    onDownload={handleDownloadPDF}
                  />
                  )}
                </>
              )}
              {!isAuthenticated ? <ThemeToggle /> : null}
              <AccountDropdown
                onLogout={handleLogout}
                onDeleteAccount={() => setShowDeleteAccountModal(true)}
                isLoggingOut={isLoggingOut}
              />
              {/* Logo Mood para tablet/desktop - dentro de header-actions */}
              <MoodLogoWithTooltip
                isDarkMode={isDarkMode}
                className="header-mood-logo header-mood-logo-desktop"
                />
            </div>
          </div>
          {isAuthenticated && userRole !== 'admin' && userRole !== 'editor' && (
            <ChatWidget userId={userId} />
          )}
          {isAuthenticated && userRole !== 'admin' && userRole !== 'editor' && (
            <ProfileForm 
              onSubmit={handleFormSubmit} 
              userEmail={userEmail || undefined}
              initialData={initialProfileData}
              onLogoUpload={handleLogoUpload}
              onGetFormData={handleSetGetFormData}
              onWantsToManageProfileChange={handleWantsToManageProfileChange}
              beforeNavigateToNetworking={requestNavigateToNetworking}
              openNetworkingTabSignal={networkingTabOpenSignal}
            />
          )}
        </>
      )}
      <Modal
        isOpen={showSpeakerValidationModal}
        onClose={() => {
          setShowSpeakerValidationModal(false);
          setPendingFormData(null);
          setIncompleteTabs([]);
        }}
        onConfirm={handleConfirmSave}
        confirmText="Guardar de todas formas"
        cancelText="Cancelar"
      >
        <p style={{ marginBottom: '12px' }}>
          En este momento hay campos obligatorios (<span style={{ color: '#CE2B15', fontWeight: 600 }}>*</span>) sin completar en:
        </p>
        <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
          {incompleteTabs.map((tab, index) => (
            <li key={index} style={{ marginBottom: '8px' }}>
              <strong>{tab}</strong>
            </li>
          ))}
        </ul>
        <p style={{ marginTop: '12px' }}>
          Se guarda tu perfil y después podés completar los campos faltantes.
        </p>
      </Modal>
      <Modal
        isOpen={showUnsavedChangesModal}
        onClose={handleCancelExit}
        customFooter={
          <div className="unsaved-changes-modal-footer">
            <button
              type="button"
              className="modal-button modal-button-secondary unsaved-changes-btn"
              onClick={handleCancelExit}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="modal-button modal-button-secondary unsaved-changes-btn unsaved-changes-btn--warn"
              onClick={handleExitWithoutSaving}
            >
              {unsavedModalContext === 'networking'
                ? `Ir a ${NETWORKING_MAIN_TAB_LABEL} sin guardar`
                : unsavedModalContext === 'logout'
                  ? 'Cerrar sesión sin guardar'
                  : 'Salir sin guardar'}
            </button>
            <button
              type="button"
              className="modal-button unsaved-changes-btn unsaved-changes-btn--primary"
              onClick={handleConfirmSaveBeforeExit}
            >
              Guardar cambios
            </button>
          </div>
        }
      >
        <p style={{ marginBottom: '12px' }}>
          Tenés cambios sin guardar en tu perfil.
        </p>
        <p style={{ marginTop: '12px', marginBottom: '0' }}>
          {unsavedModalContext === 'networking' && (
            <>
              ¿Querés guardar los cambios antes de ir a la pestaña {NETWORKING_MAIN_TAB_LABEL}?
            </>
          )}
          {unsavedModalContext === 'logout' && (
            <>¿Querés guardar los cambios antes de cerrar sesión?</>
          )}
          {unsavedModalContext == null && (
            <>¿Querés guardar los cambios antes de salir?</>
          )}
        </p>
      </Modal>
      <Modal
        isOpen={showDraftModal}
        onClose={() => {
          // No permitir cerrar el modal sin elegir una opción
        }}
        title="Guardar perfil"
        customFooter={
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', width: '100%' }}>
          <button
            className="modal-button modal-button-secondary"
            onClick={() => handleDraftChoice(true)}
            disabled={savingDraft}
            style={{ opacity: savingDraft ? 0.6 : 1, cursor: savingDraft ? 'not-allowed' : 'pointer' }}
          >
            {savingDraft ? 'Guardando...' : 'Guardar como borrador'}
          </button>
          <button
            className="modal-button"
            onClick={() => handleDraftChoice(false)}
            disabled={savingDraft}
            style={{ opacity: savingDraft ? 0.6 : 1, cursor: savingDraft ? 'not-allowed' : 'pointer' }}
          >
            {savingDraft ? 'Guardando...' : 'Guardar y autorizar su difusión'}
          </button>
        </div>
        }
      >
        <p style={{ color: '#1e293b', marginBottom: '0', lineHeight: '1.6' }}>
          ¿Cómo querés guardar tu perfil?
        </p>
      </Modal>

      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeletingAccount}
      />

      <ValidationErrorsModal
        isOpen={showValidationErrorsModal}
        onClose={() => {
          setShowValidationErrorsModal(false);
          setPendingSaveData(null);
          setValidationErrors([]);
        }}
        errors={validationErrors}
        onClearAndSave={handleClearErrorsAndSave}
      />

      <Modal
        isOpen={showNetworkingEntryPrompt}
        onClose={dismissNetworkingEntryPrompt}
        title={NETWORKING_MAIN_TAB_LABEL}
        contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
        customFooter={
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', width: '100%' }}>
            <button
              type="button"
              className="modal-button modal-button-secondary"
              onClick={dismissNetworkingEntryPrompt}
            >
              Entendido
            </button>
            <button
              type="button"
              className="modal-button"
              onClick={goToNetworkingFromEntryPrompt}
            >
              Ir a {NETWORKING_MAIN_TAB_LABEL}
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          Ya podés agendar reuniones durante los días del evento.
        </p>
      </Modal>
    </div>
  );
};

export default App;
